import { useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router';
import {
  Bot,
  Plus,
  Target,
  BarChart3,
  Play,
  Square,
  ArrowDownToLine,
  ArrowUpFromLine,
  AlertTriangle,
  ArrowUpCircle,
  Loader2,
  Archive,
  Lock,
  Server,
  ExternalLink,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useBots,
  useArchivedBots,
  useStartBot,
  useStopBot,
  useArchiveBot,
  useDepositBot,
  useWithdrawBot,
  useBotOpenPositions,
} from '@/hooks/use-bots';
import { useMt5Accounts } from '@/hooks/use-accounts';
import { useEngineUpdates, useApplyEngineUpdate } from '@/hooks/use-engine-updates';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Modal } from '@/components/ui/modal';
import { Input } from '@/components/ui/input';
import { usePlanLimits } from '@/hooks/use-plan-limits';
import { Tooltip } from '@/components/ui/tooltip';
import { formatCurrency, formatPnl, formatPct, pnlColor, botCurrency } from '@/lib/format';
import type { BotInstance } from '@/types/api';

type ModeFilter = 'all' | 'live' | 'paper';
type StatusFilter = 'all' | 'running' | 'stopped' | 'archived';

export default function BotListPage() {
  const { data: bots, isLoading, error } = useBots({ refetchInterval: 10_000 });
  const { data: archivedBots } = useArchivedBots();
  const { data: mt5Accounts } = useMt5Accounts();
  const limits = usePlanLimits();
  const [modeFilter, setModeFilter] = useState<ModeFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [showStopAll, setShowStopAll] = useState(false);
  const hasNoAccounts = mt5Accounts !== undefined && mt5Accounts.length === 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-sm rounded-lg p-6 border-red/20 text-center">
        <p className="text-red text-sm font-semibold mb-2">Failed to load bots</p>
        <p className="text-xs text-ink-3">Check that the API server is running.</p>
      </div>
    );
  }

  const allBots = (bots ?? []).filter(b => b.strategy_type !== 'COPY_TRADE');
  const activeBotCount = allBots.filter(b => !b.archived_at).length;
  const atBotLimit = activeBotCount >= limits.maxBotsPerUser;

  const allArchived = archivedBots ?? [];

  // Apply mode filter first
  const modeScoped =
    modeFilter === 'live'
      ? allBots.filter((b) => b.botMode === 'LIVE')
      : modeFilter === 'paper'
        ? allBots.filter((b) => b.botMode === 'PAPER')
        : allBots;

  // Apply status filter
  const filtered =
    statusFilter === 'archived'
      ? allArchived
      : statusFilter === 'running'
        ? modeScoped.filter((b) => b.is_active)
        : statusFilter === 'stopped'
          ? modeScoped.filter((b) => !b.is_active)
          : modeScoped;

  // Mode counts (global)
  const modeCounts = {
    all: allBots.length,
    live: allBots.filter((b) => b.botMode === 'LIVE').length,
    paper: allBots.filter((b) => b.botMode === 'PAPER').length,
  };

  // Status counts (scoped to mode)
  const statusCounts = {
    all: modeScoped.length,
    running: modeScoped.filter((b) => b.is_active).length,
    stopped: modeScoped.filter((b) => !b.is_active).length,
    archived: allArchived.length,
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold tracking-tight">Bots</h1>
          <p className="text-xs text-ink-3 mt-0.5">
            {activeBotCount}/{limits.maxBotsPerUser} bot{limits.maxBotsPerUser !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {statusCounts.running > 0 && (
            <Button variant="danger" size="sm" onClick={() => setShowStopAll(true)}>
              <Square size={12} />
              Stop All
            </Button>
          )}
          {atBotLimit ? (
            <Button size="sm" disabled>
              <Lock size={14} />
              Limit Reached
            </Button>
          ) : (
            <Link to="/bots/new">
              <Button size="sm">
                <Plus size={14} />
                Create Bot
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Engine Update Banner */}
      <EngineUpdateBanner />

      {/* Filter Pills */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Mode filters */}
        <div className="flex gap-1.5">
          {(['all', 'live', 'paper'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setModeFilter(f)}
              className={`text-[10px] font-bold px-3 py-1.5 rounded-md transition-all cursor-pointer ${
                modeFilter === f
                  ? 'bg-orange-bg text-orange border border-orange/20'
                  : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2 hover:text-ink'
              }`}
            >
              {f === 'all' ? 'All' : f === 'live' ? 'Live' : 'Paper'}
              <span className="ml-1.5 opacity-60">{modeCounts[f]}</span>
            </button>
          ))}
        </div>

        <span className="text-ink-4 text-[10px] select-none">/</span>

        {/* Status filters */}
        <div className="flex gap-1.5">
          {(['all', 'running', 'stopped', 'archived'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`text-[10px] font-bold px-3 py-1.5 rounded-md transition-all cursor-pointer ${
                statusFilter === f
                  ? 'bg-orange-bg text-orange border border-orange/20'
                  : 'bg-surface text-ink-3 border border-glass-border hover:bg-surface-2 hover:text-ink'
              }`}
            >
              {f === 'all' ? 'All' : f === 'running' ? 'Running' : f === 'stopped' ? 'Stopped' : 'Archived'}
              <span className="ml-1.5 opacity-60">{statusCounts[f]}</span>
            </button>
          ))}
        </div>
      </div>

      {/* No MT5 account CTA */}
      {hasNoAccounts && (
        <div className="glass-sm rounded-lg p-5 text-center">
          <Server size={32} className="text-orange/40 mx-auto mb-2" />
          <h3 className="text-sm font-bold mb-1">Link an MT5 account first</h3>
          <p className="text-xs text-ink-3 mb-3">
            You need a MetaTrader 5 account to create and run bots.
          </p>
          <Link to="/accounts/new">
            <Button size="sm">
              <Plus size={14} />
              Add MT5 Account
            </Button>
          </Link>
          <div className="mt-3 pt-3 border-t border-border">
            <a
              href="https://partner-tracking.deriv.com/click?a=2204&o=1&c=3&link_id=1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange hover:text-orange/80 transition-colors"
            >
              Start with Deriv — open a free MT5 account
              <ExternalLink size={10} />
            </a>
          </div>
        </div>
      )}

      {/* Bot List */}
      {filtered.length === 0 && !hasNoAccounts ? (
        <EmptyState modeFilter={modeFilter} statusFilter={statusFilter} />
      ) : filtered.length === 0 ? null : (
        <div className="space-y-2">
          {filtered.map((bot) => (
            <BotCard key={bot.id} bot={bot} />
          ))}
        </div>
      )}

      {/* Stop All Modal */}
      <StopAllModal
        open={showStopAll}
        onClose={() => setShowStopAll(false)}
        runningBots={modeScoped.filter((b) => b.is_active)}
      />
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────

function BotCard({ bot }: { bot: BotInstance }) {
  const startBot = useStartBot();
  const stopBot = useStopBot();
  const archiveBot = useArchiveBot();
  const [showStop, setShowStop] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);

  const pnl = bot.combined_pnl ?? bot.total_pnl;
  const pnlPctValue =
    bot.assigned_capital > 0 ? (pnl / bot.assigned_capital) * 100 : 0;
  const winRate =
    bot.total_trades > 0
      ? (bot.winning_trades / bot.total_trades) * 100
      : 0;

  const unallocatedCapital = bot.mt5Account?.unallocated_capital ?? 0;

  const currency = botCurrency(bot);
  const isPaper = bot.botMode === 'PAPER';
  const isArchived = !!bot.archived_at;

  return (
    <>
      <Link
        to={`/bots/${bot.id}`}
        className={`group block rounded-lg overflow-hidden transition-all ${isArchived ? 'opacity-60' : ''}`}
      >
        <div className={`flex border border-glass-border rounded-lg glass-sm hover:border-orange/15 transition-all ${
          isArchived ? '' : bot.is_active
            ? isPaper ? 'border-l-[3px] border-l-blue' : 'border-l-[3px] border-l-green'
            : 'border-l-[3px] border-l-ink-4'
        }`}>
          <div className="flex-1 min-w-0 p-4 space-y-2">
            {/* Row 1 — Name */}
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-bold truncate group-hover:text-orange transition-colors">{bot.name}</span>
              <div className="flex items-center gap-2 text-[10px] text-ink-4 flex-shrink-0">
                <span className="flex items-center gap-1">
                  <BarChart3 size={10} />
                  {bot.total_trades}
                </span>
                <span>·</span>
                <span className="flex items-center gap-1">
                  <Target size={10} />
                  {winRate.toFixed(0)}%
                </span>
              </div>
            </div>

            {/* Row 2 — Badges */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {isArchived ? (
                <Badge color="default">Archived</Badge>
              ) : (
                <>
                  {bot.is_active ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded text-green bg-green-bg border border-green/20">
                      <span className="w-1.5 h-1.5 rounded-full bg-green animate-pulse" />
                      Running
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-ink-4 bg-surface-2 border border-glass-border">
                      Stopped
                    </span>
                  )}
                  {isPaper ? (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-blue bg-blue-bg border border-blue/20">
                      Paper
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded text-green bg-green-bg border border-green/20">
                      Live
                    </span>
                  )}
                </>
              )}
              <Badge color="blue">{bot.symbol}</Badge>
            </div>

            {/* Row 3 — Equity, P&L, actions */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-[11px] font-mono text-ink-3">
                {formatCurrency(bot.virtual_equity, currency)} <span className="text-ink-4">equity</span>
              </span>

              <span className={`text-sm font-bold font-mono ${pnlColor(pnl)}`}>
                {formatPnl(pnl, currency)}
              </span>
              <span className={`text-[10px] font-semibold -ml-1.5 ${pnlColor(pnlPctValue)}`}>
                ({formatPct(pnlPctValue)})
              </span>

              <div className="flex-1" />

              {/* Action Buttons */}
              {!isArchived && (
                <div
                  className="flex items-center gap-0.5 flex-shrink-0"
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                >
                  {bot.is_active ? (
                    <Tooltip content="Stop">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowStop(true);
                        }}
                        className="w-7 h-7 rounded-md flex items-center justify-center text-ink-4 hover:text-red hover:bg-red/10 transition-colors cursor-pointer"
                      >
                        <Square size={12} />
                      </button>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip content="Start">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            startBot.mutate(bot.id);
                          }}
                          disabled={startBot.isPending}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-ink-4 hover:text-green hover:bg-green/10 transition-colors cursor-pointer disabled:opacity-50"
                        >
                          {startBot.isPending ? (
                            <Spinner size="sm" />
                          ) : (
                            <Play size={12} />
                          )}
                        </button>
                      </Tooltip>
                      <Tooltip content="Archive">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowArchive(true);
                          }}
                          className="w-7 h-7 rounded-md flex items-center justify-center text-ink-4 hover:text-amber hover:bg-amber/10 transition-colors cursor-pointer"
                        >
                          <Archive size={12} />
                        </button>
                      </Tooltip>
                    </>
                  )}
                  <Tooltip content="Deposit">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowDeposit(true);
                      }}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-ink-4 hover:text-orange hover:bg-orange/10 transition-colors cursor-pointer"
                    >
                      <ArrowDownToLine size={12} />
                    </button>
                  </Tooltip>
                  <Tooltip content="Withdraw">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowWithdraw(true);
                      }}
                      className="w-7 h-7 rounded-md flex items-center justify-center text-ink-4 hover:text-orange hover:bg-orange/10 transition-colors cursor-pointer"
                    >
                      <ArrowUpFromLine size={12} />
                    </button>
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* Stop Confirmation Modal */}
      <StopModal
        open={showStop}
        onClose={() => setShowStop(false)}
        onConfirm={() => {
          stopBot.mutate(bot.id);
          setShowStop(false);
        }}
        isPending={stopBot.isPending}
      />

      {/* Deposit Modal */}
      <AmountModal
        open={showDeposit}
        onClose={() => setShowDeposit(false)}
        title="Deposit Capital"
        actionLabel="Deposit"
        botId={bot.id}
        mode="deposit"
        availableBalance={unallocatedCapital}
        currency={currency}
      />

      {/* Withdraw Modal */}
      <AmountModal
        open={showWithdraw}
        onClose={() => setShowWithdraw(false)}
        title="Withdraw Capital"
        actionLabel="Withdraw"
        botId={bot.id}
        mode="withdraw"
        availableBalance={bot.virtual_equity}
        currency={currency}
      />

      {/* Archive Confirmation Modal */}
      <Modal
        open={showArchive}
        onClose={() => setShowArchive(false)}
        title="Archive Bot"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowArchive(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => {
                archiveBot.mutate(bot.id, {
                  onSuccess: () => toast.success(`"${bot.name}" archived`),
                  onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to archive bot'),
                });
                setShowArchive(false);
              }}
              disabled={archiveBot.isPending}
              variant="danger"
            >
              <Archive size={12} />
              {archiveBot.isPending ? 'Archiving...' : 'Archive Bot'}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold mb-1">Archive "{bot.name}"?</p>
            <p className="text-xs text-ink-3">
              This bot will be archived. All trade history and stats will be preserved
              and remain viewable. Assigned capital will be returned to the account.
              Archived bots cannot be restarted.
            </p>
          </div>
        </div>
      </Modal>

      {/* Start error */}
      {startBot.isError && (
        <div className="ml-14 -mt-1 mb-1">
          <p className="text-[10px] text-red">
            {(startBot.error as Error)?.message ?? 'Failed to start bot'}
          </p>
        </div>
      )}
    </>
  );
}

// ─── Modals ─────────────────────────────────────────

function StopModal({
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Stop Bot"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm} disabled={isPending}>
            <Square size={12} />
            {isPending ? 'Stopping...' : 'Stop Bot'}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-amber flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold mb-1">Are you sure?</p>
          <p className="text-xs text-ink-3">
            This will deactivate trading and close all open positions. The bot can be
            restarted later.
          </p>
        </div>
      </div>
    </Modal>
  );
}

function StopAllModal({
  open,
  onClose,
  runningBots,
}: {
  open: boolean;
  onClose: () => void;
  runningBots: BotInstance[];
}) {
  const stopBot = useStopBot();
  const qc = useQueryClient();
  const [stopping, setStopping] = useState(false);
  const [progress, setProgress] = useState(0);

  const handleStopAll = async () => {
    setStopping(true);
    setProgress(0);
    for (let i = 0; i < runningBots.length; i++) {
      try {
        const bot = runningBots[i];
        if (bot) await stopBot.mutateAsync(bot.id);
      } catch {
        // Continue stopping remaining bots even if one fails
      }
      setProgress(i + 1);
    }
    // Always refresh bot list after all stops complete, even if some failed
    await qc.invalidateQueries({ queryKey: ['bots'] });
    setStopping(false);
    setProgress(0);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={() => !stopping && onClose()}
      title="Stop All Bots"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={stopping}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={handleStopAll} disabled={stopping}>
            <Square size={12} />
            {stopping
              ? `Stopping ${progress}/${runningBots.length}...`
              : `Stop ${runningBots.length} Bot${runningBots.length !== 1 ? 's' : ''}`}
          </Button>
        </>
      }
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-red flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold mb-1">Stop all running bots?</p>
          <p className="text-xs text-ink-3">
            This will stop {runningBots.length} running bot{runningBots.length !== 1 ? 's' : ''} and
            close all their open positions. Bots can be restarted individually later.
          </p>
        </div>
      </div>
    </Modal>
  );
}

function AmountModal({
  open,
  onClose,
  title,
  actionLabel,
  botId,
  mode,
  availableBalance,
  currency = 'USD',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  actionLabel: string;
  botId: string;
  mode: 'deposit' | 'withdraw';
  availableBalance: number;
  currency?: string;
}) {
  const [amount, setAmount] = useState('');
  const deposit = useDepositBot();
  const withdraw = useWithdrawBot();
  const mutation = mode === 'deposit' ? deposit : withdraw;

  const { data: openPositions } = useBotOpenPositions(botId, {
    refetchInterval: undefined,
  });
  const hasOpenPositions = mode === 'withdraw' && (openPositions?.length ?? 0) > 0;

  const parsedAmount = parseFloat(amount);
  const isOverLimit = !isNaN(parsedAmount) && parsedAmount > availableBalance + 0.01;

  const handleSubmit = () => {
    if (isNaN(parsedAmount) || parsedAmount <= 0 || isOverLimit) return;
    mutation.mutate(
      { id: botId, amount: parsedAmount },
      {
        onSuccess: () => {
          setAmount('');
          onClose();
        },
      },
    );
  };

  const handleMax = () => {
    if (availableBalance > 0) {
      setAmount(availableBalance.toFixed(2));
    }
  };

  const balanceLabel =
    mode === 'deposit' ? 'Available (unallocated)' : 'Available equity';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSubmit}
            disabled={mutation.isPending || !amount || parsedAmount <= 0 || isOverLimit}
          >
            {mutation.isPending ? 'Processing...' : actionLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {hasOpenPositions && (
          <div className="flex items-start gap-2 bg-amber/10 border border-amber/20 rounded-lg px-3 py-2">
            <AlertTriangle size={14} className="text-amber flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber font-semibold leading-relaxed">
              This bot has {openPositions!.length} open position{openPositions!.length > 1 ? 's' : ''}.
              Withdrawing capital reduces available margin and may trigger the equity stop-loss.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between bg-surface/50 rounded-lg px-3 py-2">
          <span className="text-[10px] font-semibold text-ink-4 uppercase tracking-wider">
            {balanceLabel}
          </span>
          <span className="text-sm font-bold font-mono text-ink">
            {formatCurrency(availableBalance, currency)}
          </span>
        </div>

        <div className="space-y-1">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label={`Amount (${currency})`}
                type="number"
                placeholder="0.00"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={
                  isOverLimit
                    ? `Exceeds available ${formatCurrency(availableBalance, currency)}`
                    : mutation.isError
                      ? 'Transfer failed. Check available balance.'
                      : undefined
                }
              />
            </div>
            <button
              type="button"
              onClick={handleMax}
              className="mb-[2px] text-[10px] font-bold text-orange hover:text-orange-light transition-colors cursor-pointer px-2 py-1.5 rounded bg-orange-bg/50 hover:bg-orange-bg"
            >
              MAX
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Engine Update Banner ────────────────────────────

function EngineUpdateBanner() {
  const { data: update, isLoading } = useEngineUpdates();
  const { mutate, isUpdating, updateFailed, allWorkersOffline, secondsRemaining } = useApplyEngineUpdate(update);

  if (isLoading) return null;

  // Show banner when update available OR actively updating (survives page refresh)
  if (!update?.updateAvailable && !isUpdating) return null;

  if (isUpdating) {
    return (
      <div className="glass-sm rounded-lg border border-blue/20 bg-blue/5 p-4">
        <div className="flex items-center gap-3">
          <Loader2 size={20} className="text-blue flex-shrink-0 animate-spin" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold mb-0.5">
              Updating to v{update?.latestVersion ?? '...'}
            </p>
            <p className="text-xs text-ink-3">
              Engine is restarting and reconnecting to brokers
              {secondsRemaining > 0 && <span className="text-ink-4"> ({secondsRemaining}s)</span>}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`glass-sm rounded-lg border p-4 ${
      updateFailed ? 'border-red/20 bg-red/5' : 'border-blue/20 bg-blue/5'
    }`}>
      <div className="flex items-start gap-3">
        {updateFailed ? (
          <AlertTriangle size={20} className="text-red flex-shrink-0 mt-0.5" />
        ) : (
          <ArrowUpCircle size={20} className="text-blue flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold mb-0.5">
            Engine v{update!.latestVersion} available
          </p>
          {updateFailed ? (
            <p className="text-xs text-red/80 mb-2">
              Update failed — {allWorkersOffline
                ? 'engine workers are offline. Check that the orchestrator is running on the VPS.'
                : 'workers did not respond. They may have crashed during restart.'}
            </p>
          ) : (
            <p className="text-xs text-ink-3 mb-2">{update!.changelog}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-ink-4 mb-3">
            {update!.totalOpenPositions > 0 && (
              <span>
                {update!.totalOpenPositions} open position{update!.totalOpenPositions !== 1 ? 's' : ''} across{' '}
                {update!.totalActiveBots} active bot{update!.totalActiveBots !== 1 ? 's' : ''}
              </span>
            )}
            <span>Estimated downtime: {update!.estimatedDowntime}</span>
            <span>Positions are preserved (broker-side)</span>
          </div>
          <Button
            size="sm"
            onClick={() => mutate()}
          >
            {updateFailed ? 'Retry Update' : 'Update Now'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Empty State ────────────────────────────────────

function EmptyState({
  modeFilter,
  statusFilter,
}: {
  modeFilter: ModeFilter;
  statusFilter: StatusFilter;
}) {
  const modeLabel =
    modeFilter === 'live' ? 'live' : modeFilter === 'paper' ? 'paper' : '';
  const isFiltered = modeFilter !== 'all' || statusFilter !== 'all';

  let title: string;
  let description: string;

  if (!isFiltered) {
    title = 'No bots yet';
    description = 'Create a strategy and deploy your first trading bot.';
  } else if (statusFilter === 'archived') {
    title = 'No archived bots';
    description = 'Archived bots will appear here.';
  } else if (statusFilter === 'running') {
    title = `No running ${modeLabel} bots`.replace(/\s+/g, ' ').trim();
    description = 'Start a bot to begin trading.';
  } else if (statusFilter === 'stopped') {
    title = `No stopped ${modeLabel} bots`.replace(/\s+/g, ' ').trim();
    description = 'All matching bots are currently running.';
  } else {
    title = `No ${modeLabel} bots`.trim();
    description =
      modeFilter === 'paper'
        ? 'Create a bot in paper mode to test strategies without risking real capital.'
        : modeFilter === 'live'
          ? 'Create a bot in live mode to begin real trading.'
          : 'Create a strategy and deploy your first trading bot.';
  }

  return (
    <div className="glass-sm rounded-lg p-10 text-center">
      <Bot size={40} className="text-orange/40 mx-auto mb-3" />
      <h3 className="text-sm font-bold mb-1">{title}</h3>
      <p className="text-xs text-ink-3 mb-4">{description}</p>
      {!isFiltered && (
        <Link to="/bots/new">
          <Button size="sm">
            <Plus size={14} />
            Create Bot
          </Button>
        </Link>
      )}
    </div>
  );
}
